import { ArrowDown, ArrowUp } from 'lucide-react'

export type RuleMode = 'each' | 'team' | 'rotation' | 'open'

export type Cadence =
  | { type: 'daily' }
  | { type: 'weekdays'; days: number[] }
  | { type: 'weekly'; day: number }

export interface ChoreRule {
  mode: RuleMode
  member_ids: string[]
  start_member_id?: string | null
  cadence: Cadence
  due_time?: string | null
  is_active?: boolean
}

export interface RuleKid {
  id: string
  display_name?: string
  username?: string
}

export const DEFAULT_RULE: ChoreRule = {
  mode: 'each',
  member_ids: [],
  start_member_id: null,
  cadence: { type: 'daily' },
  due_time: null,
}

const MODES: { value: RuleMode; label: string; hint: string }[] = [
  { value: 'each', label: 'Each kid', hint: 'Every selected kid gets their own copy' },
  { value: 'team', label: 'Team', hint: 'One shared task, points split' },
  { value: 'rotation', label: 'Take turns', hint: 'One kid per day, in your order' },
  { value: 'open', label: 'Open to claim', hint: 'First to claim it gets it' },
]

// ISO weekdays: 1 = Monday ... 7 = Sunday
const DAYS = [
  { n: 1, short: 'Mon' },
  { n: 2, short: 'Tue' },
  { n: 3, short: 'Wed' },
  { n: 4, short: 'Thu' },
  { n: 5, short: 'Fri' },
  { n: 6, short: 'Sat' },
  { n: 7, short: 'Sun' },
]

const kidName = (k?: RuleKid) => k?.display_name || k?.username || 'Unknown'

/** One-line summary such as "Each kid (3), Mon/Wed/Fri". */
export function describeRule(rule: { mode: RuleMode; member_count?: number; cadence: Cadence }): string {
  const mode = MODES.find(m => m.value === rule.mode)?.label ?? rule.mode
  const count = rule.member_count != null ? ` (${rule.member_count})` : ''
  let when = 'daily'
  if (rule.cadence.type === 'weekdays') {
    when = rule.cadence.days.map(d => DAYS.find(x => x.n === d)?.short).join('/')
  } else if (rule.cadence.type === 'weekly') {
    when = `weekly on ${DAYS.find(x => x.n === (rule.cadence as { day: number }).day)?.short}`
  }
  return `${mode}${count}, ${when}`
}

export interface RuleMember { id: string; name: string | null; avatar_url?: string | null }

/** Members in rule order, e.g. "Casey, Jordan, Morgan". Empty when the API sent none. */
export function ruleMemberNames(rule: { members?: RuleMember[] }): string {
  return (rule.members ?? []).map(m => m.name || 'Unknown').join(', ')
}

/** "Next: Morgan" for a rotation, with the day when it is not today. Null otherwise. */
export function ruleNextLabel(
  rule: { mode: RuleMode; next?: (RuleMember & { date?: string | null }) | null },
  today: Date = new Date(),
): string | null {
  if (rule.mode !== 'rotation' || !rule.next) return null
  const name = rule.next.name || 'Unknown'
  const date = rule.next.date
  if (!date) return `Next: ${name}`
  const [y, m, d] = date.split('-').map(Number)
  const day = new Date(y, m - 1, d)
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const diff = Math.round((day.getTime() - startOfToday.getTime()) / 86400000)
  if (diff <= 0) return `Next: ${name} (today)`
  if (diff === 1) return `Next: ${name} (tomorrow)`
  return `Next: ${name} (${day.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })})`
}

/** Returns an error message, or null when the rule can be saved. */
export function validateRule(rule: ChoreRule): string | null {
  if (rule.member_ids.length === 0) return 'Pick at least one kid.'
  if (rule.cadence.type === 'weekdays' && rule.cadence.days.length === 0) return 'Pick at least one day.'
  return null
}

interface Props {
  kids: RuleKid[]
  value: ChoreRule
  onChange: (rule: ChoreRule) => void
}

export function ChoreRuleEditor({ kids, value, onChange }: Props) {
  const set = (patch: Partial<ChoreRule>) => onChange({ ...value, ...patch })
  const byId = new Map(kids.map(k => [k.id, k]))

  // Selected kids in rule order, then everyone else.
  const selected = value.member_ids.filter(id => byId.has(id))
  const unselected = kids.filter(k => !selected.includes(k.id))

  const toggleKid = (id: string) => {
    const members = selected.includes(id) ? selected.filter(m => m !== id) : [...selected, id]
    const start = value.start_member_id && members.includes(value.start_member_id) ? value.start_member_id : null
    set({ member_ids: members, start_member_id: start })
  }

  const move = (idx: number, delta: number) => {
    const next = [...selected]
    const target = idx + delta
    if (target < 0 || target >= next.length) return
    ;[next[idx], next[target]] = [next[target], next[idx]]
    set({ member_ids: next })
  }

  const setCadenceType = (type: Cadence['type']) => {
    if (type === 'daily') set({ cadence: { type } })
    else if (type === 'weekdays') set({ cadence: { type, days: [1, 2, 3, 4, 5] } })
    else set({ cadence: { type, day: 6 } })
  }

  const toggleDay = (n: number) => {
    if (value.cadence.type !== 'weekdays') return
    const days = value.cadence.days.includes(n)
      ? value.cadence.days.filter(d => d !== n)
      : [...value.cadence.days, n].sort()
    set({ cadence: { type: 'weekdays', days } })
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">How is it shared?</label>
        <div className="grid grid-cols-2 gap-2">
          {MODES.map(m => (
            <button
              key={m.value}
              type="button"
              onClick={() => set({ mode: m.value })}
              className={`text-left px-3 py-2 rounded-lg border ${
                value.mode === m.value ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-sm font-medium">{m.label}</div>
              <div className="text-xs text-gray-500">{m.hint}</div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          {value.mode === 'rotation' ? 'Who takes turns, in order' : 'Which kids'}
        </label>
        <div className="space-y-1">
          {selected.map((id, idx) => (
            <div key={id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50">
              <input type="checkbox" checked onChange={() => toggleKid(id)} aria-label={`Remove ${kidName(byId.get(id))}`} />
              {value.mode === 'rotation' && <span className="text-xs text-gray-500 w-5">{idx + 1}.</span>}
              <span className="flex-1 text-sm">{kidName(byId.get(id))}</span>
              {value.mode === 'rotation' && (
                <>
                  <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 text-gray-500 disabled:opacity-30" aria-label="Move up">
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => move(idx, 1)} disabled={idx === selected.length - 1} className="p-1 text-gray-500 disabled:opacity-30" aria-label="Move down">
                    <ArrowDown className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          ))}
          {unselected.map(k => (
            <label key={k.id} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={false} onChange={() => toggleKid(k.id)} />
              <span className="text-sm text-gray-700">{kidName(k)}</span>
            </label>
          ))}
          {kids.length === 0 && <p className="text-sm text-gray-500">Add kids to your family first.</p>}
        </div>
        {kids.length > 1 && (
          <button
            type="button"
            onClick={() => set({ member_ids: kids.map(k => k.id) })}
            className="mt-2 text-sm text-blue-600 hover:text-blue-800"
          >
            Select all kids
          </button>
        )}
      </div>

      {value.mode === 'rotation' && selected.length > 1 && (
        <div>
          <label className="block text-sm font-medium mb-1">Starts with</label>
          <select
            value={value.start_member_id || selected[0]}
            onChange={e => set({ start_member_id: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
          >
            {selected.map(id => (
              <option key={id} value={id}>
                {kidName(byId.get(id))}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            If a kid is away on their day, the next kid covers it and they take the next day they are home.
          </p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium mb-2">How often</label>
        <div className="flex gap-2">
          {(['daily', 'weekdays', 'weekly'] as const).map(t => (
            <button
              key={t}
              type="button"
              onClick={() => setCadenceType(t)}
              className={`px-3 py-1.5 rounded-lg text-sm border ${
                value.cadence.type === t ? 'border-blue-600 bg-blue-50' : 'border-gray-200'
              }`}
            >
              {t === 'daily' ? 'Every day' : t === 'weekdays' ? 'Pick days' : 'Once a week'}
            </button>
          ))}
        </div>
        {value.cadence.type === 'weekdays' && (
          <div className="flex flex-wrap gap-1 mt-2">
            {DAYS.map(d => (
              <button
                key={d.n}
                type="button"
                onClick={() => toggleDay(d.n)}
                className={`px-2.5 py-1 rounded text-xs border ${
                  value.cadence.type === 'weekdays' && value.cadence.days.includes(d.n)
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'border-gray-200'
                }`}
              >
                {d.short}
              </button>
            ))}
          </div>
        )}
        {value.cadence.type === 'weekly' && (
          <select
            value={value.cadence.day}
            onChange={e => set({ cadence: { type: 'weekly', day: Number(e.target.value) } })}
            className="mt-2 px-3 py-2 border rounded-lg"
          >
            {DAYS.map(d => (
              <option key={d.n} value={d.n}>
                {d.short}
              </option>
            ))}
          </select>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Due time (optional)</label>
        <input
          type="time"
          value={value.due_time || ''}
          onChange={e => set({ due_time: e.target.value || null })}
          className="px-3 py-2 border rounded-lg"
        />
        <p className="text-xs text-gray-500 mt-1">Leave blank to use the chore's default due time.</p>
      </div>
    </div>
  )
}
